import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { base as duiBase } from "@dui/primitives/core";

/** Sent when the person asks to sign in with Google. */
export type SignInEvent = CustomEvent<undefined>;

/**
 * Google sign-in card. Data down, events up: the parent supplies `error` and
 * `submitting`, and hears `sign-in` when the person presses the button.
 */
@customElement("sign-in-form")
export class SignInForm extends LitElement {
  static override styles = [
    duiBase,
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 24rem;
      }
      .content {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      .error {
        color: var(--destructive-text);
        font-size: var(--text-sm);
      }
      /* A card pads its bottom only through its footer. */
      dui-card::part(content) {
        padding-bottom: var(--card-py-bottom);
      }
      dui-button {
        display: block;
      }
      dui-button::part(root) {
        width: 100%;
      }
    `,
  ];

  @property({ type: String })
  accessor error: string | undefined = undefined;

  @property({ type: Boolean })
  accessor submitting = false;

  #handleClick = (): void => {
    if (this.submitting) return;
    this.dispatchEvent(new CustomEvent("sign-in"));
  };

  override render(): TemplateResult {
    return html`
      <dui-card>
        <span slot="title">Sign in to ETO</span>
        <div class="content">
          <dui-button
            variant="primary"
            ?disabled=${this.submitting}
            @click=${this.#handleClick}
          >
            ${this.submitting
              ? html`<dui-spinner size="sm"></dui-spinner>`
              : "Continue with Google"}
          </dui-button>
          ${this.error !== undefined
            ? html`<p class="error" role="alert">${this.error}</p>`
            : nothing}
        </div>
      </dui-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sign-in-form": SignInForm;
  }
}
